import React, { useEffect, useRef, useState, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { message } from 'antd';
import SubmitBtn from '@/components/SubmitBtn';
import { handleQueryParams, ClassNames, handleInstaLink } from '@/utils';
import styles from './index.module.less';
import Loading from '@/components/SpinLoading';
import ExchangeModal from './components/ExchangeModal';
import ExchangeThirdGiftCardModal from './components/ExchangeThirdGiftCardModal';
import ExchangeSuccessModal from './components/ExchangeSuccessModal';
import ExchangePhysicalProductModal from './components/ExchangePhysicalProductModal';
import tracking from './tracking';

const PeriodTime = ({ item, text }) => {
  const { period, periodTime } = item;
  if (!period && periodTime) return null;
  if (period && !periodTime) return null;
  if (!period && !periodTime) return null;
  return <div className={styles.remark}>*{text.replace('%d', period).replace('%t', periodTime)}</div>;
};

function CoverWithSuperscript({ productDetail }) {
  return (
    <div className={styles.cover}>
      <img className={styles.cover_main} src={productDetail?.cover || ''} alt='' />
      {productDetail?.info?.superscript ? (
        <img className={styles.superscript} src={productDetail.info.superscript} alt='' />
      ) : null}
    </div>
  );
}

const Product = inject(
  'area',
  'gold',
  'auth',
  'app'
)(
  observer((props) => {
    const {
      LANG,
      country: { code },
    } = props.area;
    const { isMobile, urlParams } = props.app;
    const { alreadyAuth } = props.auth;
    const { getProductExchangeTimes } = props.gold;
    const [productDetail, setProductDetail] = useState({});

    const ExchangeModalRef = useRef(null);
    const ExchangeThirdGiftCardModalRef = useRef(null);
    const ExchangePhysicsProductModalRef = useRef(null);
    const SuccessModalRef = useRef(null);

    useEffect(() => {
      init();
    }, []);

    // 初始化产品信息
    const init = async () => {
      const { productData } = props;

      console.log(productData);

      // 没有库存则禁用（按钮置灰，不允许点击）
      productData.disabled = productData.repertory <= 0;

      if (productData.disabled) {
        return setProductDetail(productData);
      }

      // 只有登录用户去做校验，判断限购产品是否已经没有剩余兑换次数
      // period：xxx天 ； periodTime: xxx次 （xx天限购xx次）
      if (alreadyAuth && productData?.period && productData?.periodTime) {
        const exchangeTimes = await getProductExchangeTimes(productData.id);
        productData.exchangeTimes = exchangeTimes;
        productData.noRedemptionTimes = exchangeTimes >= productData.periodTime;
        productData.disabled = productData.noRedemptionTimes;
      }
      setProductDetail(productData);
    };

    useEffect(() => {
      if (urlParams?.action === 'buy') {
        handleExchangeBtnClick();
        window.history.pushState({}, null, window.location.origin + window.location.pathname);
      }
    }, [productDetail]);

    // 立即兑换 - 普通积分商品（直接兑换） || 第三方礼品卡（填写邮箱和选择地区）
    const handleExchangeBtnClick = () => {
      if (productDetail.disabled) return;
      if (!alreadyAuth) {
        window.location.href = handleInstaLink(
          'www',
          `/account/login?redirect_url=${handleQueryParams('action', 'buy')}`,
          props.app
        );
        return;
      }

      switch (productDetail.productType) {
        case 'insta360_coupon':
          ExchangeModalRef.current.toggleVisible(true);
          break;
        case 'third_egift_card':
          ExchangeThirdGiftCardModalRef.current.toggleVisible(true);
          break;
        case 'physical_product':
          ExchangePhysicsProductModalRef.current.toggleVisible(true);
          break;
        default:
      }

      tracking.redeemButtonClick({
        itemId: productDetail.id,
        location: code,
      });
    };

    // 兑换成功后是否有提示内容（兑换咖啡券后有客服微信二维码显示）
    const [hasExchangeSuccessTip, exchangeSuccessTip] = useMemo(
      () => [!!productDetail?.info?.tipsContent?.trim(), productDetail?.info?.tipsContent],
      [productDetail?.info?.tipsContent]
    );

    if (!productDetail.id) return <Loading />;
    return (
      <div className={ClassNames([styles.product, isMobile ? styles.mobile : ''])}>
        <div className={styles.wrapper}>
          <CoverWithSuperscript productDetail={productDetail} />

          <div className={styles.info}>
            <p className={styles.name}>{productDetail?.info?.title}</p>
            <p className={styles.point}>
              {LANG['common.label.gold']}: {productDetail?.points}
            </p>
            <p className={styles.stock}>
              {LANG['common.label.stock']}: <span>{productDetail?.repertory}</span>
            </p>
            <p className={styles.desc} dangerouslySetInnerHTML={{ __html: productDetail?.info?.description }} />
            <div className={styles.buyWrap}>
              {isMobile && (
                <span className={styles.barPoint}>
                  {LANG['common.label.gold']}: {productDetail.points}
                </span>
              )}
              <SubmitBtn
                className={styles.submitBtn}
                onClick={handleExchangeBtnClick}
                disabled={productDetail.disabled}
              >
                {productDetail.repertory === 0
                  ? LANG['common.label.outOfStock']
                  : productDetail.noRedemptionTimes
                  ? LANG['product.notAvailable']
                  : LANG['common.btn.redeem']}
              </SubmitBtn>
            </div>
            <p className={styles.remark}>{LANG['product.stockTips']}</p>
            <PeriodTime item={productDetail} text={LANG['product.redeemedMax']} />
          </div>
        </div>
        <ExchangeModal
          ref={ExchangeModalRef}
          productDetail={productDetail}
          onExchangeProductSuccess={() => SuccessModalRef.current.toggleVisible(true)}
        />
        <ExchangeThirdGiftCardModal
          ref={ExchangeThirdGiftCardModalRef}
          productDetail={productDetail}
          onExchangeProductSuccess={() => {
            // 如果产品有兑换成功提示则显示弹窗提示
            if (!!hasExchangeSuccessTip) {
              SuccessModalRef.current.toggleVisible(true, { showFooter: false });
            } else {
              // 没有则默认提示后刷新页面
              message.success(LANG['common.tips.redeemSuccessful']);
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            }
          }}
        />

        <ExchangePhysicalProductModal
          ref={ExchangePhysicsProductModalRef}
          productDetail={productDetail}
          onExchangeProductSuccess={() => {
            // 如果产品有兑换成功提示则显示弹窗提示
            if (!!hasExchangeSuccessTip) {
              SuccessModalRef.current.toggleVisible(true, { showFooter: false });
            } else {
              // 没有则默认提示后刷新页面
              message.success(LANG['common.tips.redeemSuccessful']);
              setTimeout(() => {
                window.location.reload();
              }, 1000);
            }
          }}
        />

        <ExchangeSuccessModal ref={SuccessModalRef} hasTip={hasExchangeSuccessTip} tipContent={exchangeSuccessTip} />
      </div>
    );
  })
);

export async function getServerSideProps({ res }) {
  return {
    props: {
      productData: res.pageData?.productData ?? {},
    },
  };
}

export default Product;
